/**
 * useEngagements Hook
 *
 * One-shot data loader for engagement sessions.
 * Used by the Funnel tab (loads all for stats) and Sessions tab (paged list).
 *
 * Not real-time — call refresh() to reload.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db, functions } from '../firebase';
import { EngagementRepository } from '../models/EngagementRepository';

/**
 * @param {Object}  options
 * @param {string}  [options.environment='production']  Filter by environment
 * @param {Date}    [options.startDate]                 Lower bound on lastSeen
 * @param {Date}    [options.endDate]                   Upper bound on lastSeen
 * @param {boolean} [options.convertedOnly=false]       Only converted sessions
 * @param {number}  [options.limit]                     Cap result count
 * @param {boolean} [options.immediate=true]            Load on mount
 *
 * @returns {{ sessions, loading, error, refresh, count }}
 */
export const useEngagements = (options = {}) => {
  const {
    environment = 'production',
    startDate,
    endDate,
    convertedOnly = false,
    limit,
    immediate = true,
  } = options;

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  // Requests are stamped so a slow earlier fetch (e.g. a 90-day scan) can't land
  // after a faster later one (Today) and overwrite it with stale numbers — the
  // range switch would otherwise silently show the previous range's figures.
  const reqRef = useRef(0);

  const repo = useMemo(() => new EngagementRepository(db, functions), []);

  const refresh = useCallback(async () => {
    const myReq = reqRef.current + 1;
    reqRef.current = myReq;
    setLoading(true);
    setError(null);
    const run = () =>
      repo.findByDateRange(startDate || null, endDate || null, environment, {
        convertedOnly,
        limit,
      });
    try {
      let results;
      try {
        results = await run();
      } catch (firstErr) {
        // One silent retry: an idle Safari tab often fails the first request on a
        // stale channel, then reconnects on the next attempt.
        console.warn('[useEngagements] first attempt failed, retrying once:', firstErr?.message);
        results = await run();
      }
      setSessions(results);
    } catch (err) {
      console.error('[useEngagements] Load error:', err);
      if (reqRef.current !== myReq) return;
      setError(err);
    } finally {
      if (reqRef.current === myReq) setLoading(false);
    }
  // Stringify dates to stabilise dependency identity across renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment, startDate?.toISOString(), endDate?.toISOString(), convertedOnly, limit]);

  useEffect(() => {
    if (immediate) {
      refresh();
    }
  }, [refresh, immediate]);

  return {
    sessions,
    loading,
    error,
    refresh,
    count: sessions.length,
    isEmpty: sessions.length === 0 && !loading,
  };
};

/**
 * useEngagementFunnelSummary — server-side funnel aggregation for the Funnel tab.
 *
 * Calls getEngagementFunnelSummaryCallable, which scans the sessions in the
 * range server-side and returns only the small summary object. The browser no
 * longer downloads thousands of event-bearing docs (the old timeout).
 *
 * ⚠️ `summary` is the LAST ANSWER THAT ARRIVED. While a new question is on its way, and when
 * a new question fails, it is still the answer to the OLD one (kept so that the page does not
 * blink). `summaryKey` says which question `summary` answers and `askKey` which question is
 * being asked now: a caller that labels the figures with what is asked NOW (an environment, a
 * drill-down) must show them only when the two are the same, or the label is of one read and
 * the figures of another (the second review of PR #138, finding 6).
 *
 * `summaryAt` is when that answer arrived (this browser's clock), kept with the answer: a caller that sets two
 * answers side by side can say which is the older.
 *
 * @param {Object} options { environment, startDate, endDate }
 * @returns {{ summary, summaryKey, summaryAt, askKey, loading, error, refresh }}
 */
export const useEngagementFunnelSummary = (options = {}) => {
  const { environment = 'production', startDate, endDate, enabled = true, hideInternal = true, hideBots = false, filter = null } = options;

  // the answer and the question it answers, kept as ONE value so that they cannot part
  const [answer, setAnswer] = useState({ summary: null, key: null, at: null });
  const summary = answer.summary;
  // the question as asked now: everything a request is made from
  const askKey = [
    environment,
    startDate ? startDate.toISOString() : '',
    endDate ? endDate.toISOString() : '',
    hideInternal ? 'i' : '',
    hideBots ? 'b' : '',
    filter ? `${filter.dimension}:${filter.label}` : '',
  ].join('|');
  const [loading, setLoading] = useState(enabled);
  // A QUIET refresh (refresh({ quiet: true })) reads again without `loading`: the Home Page view swaps everything for a
  // spinner while `loading`, which unmounted the A/B card and lost the reader's place (Lee, 15 Oct 2026). The last answer
  // stays drawn until the new one arrives, and `refreshing` says one is on its way.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Requests are stamped so a slow earlier fetch (e.g. a 90-day scan) can't land
  // after a faster later one (Today) and overwrite it with stale numbers — the
  // range switch would otherwise silently show the previous range's figures.
  const reqRef = useRef(0);

  const repo = useMemo(() => new EngagementRepository(db, functions), []);

  const refresh = useCallback(async (opts) => {
    // `opts` may be a click event (onClick={refresh}): only a real `quiet: true` is quiet
    const quiet = Boolean(opts) && opts.quiet === true;
    const myReq = reqRef.current + 1;
    reqRef.current = myReq;
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    const run = () =>
      repo.getFunnelSummary({
        environment,
        startDate: startDate || null,
        endDate: endDate || null,
        hideInternal,
        hideBots,
        filter,
      });
    try {
      let result;
      try {
        result = await run();
      } catch (firstErr) {
        // One silent retry for an idle/stale Safari connection.
        console.warn('[useEngagementFunnelSummary] first attempt failed, retrying once:', firstErr?.message);
        result = await run();
      }
      if (reqRef.current !== myReq) return; // superseded by a newer request
      setAnswer({ summary: result, key: askKey, at: Date.now() });
    } catch (err) {
      console.error('[useEngagementFunnelSummary] Load error:', err);
      if (reqRef.current !== myReq) return;
      setError(err);
    } finally {
      if (reqRef.current === myReq) { setLoading(false); setRefreshing(false); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askKey]);

  useEffect(() => {
    if (enabled) refresh();
  }, [refresh, enabled]);

  return { summary, summaryKey: answer.key, summaryAt: answer.at, askKey, loading, refreshing, error, refresh };
};

/**
 * Per-page module activity for the Pages-lens drill-down. Lazily fetches when a
 * `page` is set (click a page bar). Respects the same segment `filter`.
 */
export const useEngagementPageDetail = (options = {}) => {
  const { environment = 'production', startDate, endDate, page = null, filter = null } = options;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Requests are stamped so a slow earlier fetch (e.g. a 90-day scan) can't land
  // after a faster later one (Today) and overwrite it with stale numbers — the
  // range switch would otherwise silently show the previous range's figures.
  const reqRef = useRef(0);

  const repo = useMemo(() => new EngagementRepository(db, functions), []);
  const filterKey = filter ? `${filter.dimension}:${filter.label}` : null;

  const refresh = useCallback(async () => {
    if (!page) { setDetail(null); return; }
    const myReq = reqRef.current + 1;
    reqRef.current = myReq;
    setLoading(true);
    setError(null);
    try {
      const result = await repo.getPageDetail({
        environment, startDate: startDate || null, endDate: endDate || null, page, filter,
      });
      if (reqRef.current !== myReq) return; // superseded by a newer request
      setDetail(result);
    } catch (err) {
      console.error('[useEngagementPageDetail] Load error:', err);
      if (reqRef.current !== myReq) return;
      setError(err);
    } finally {
      if (reqRef.current === myReq) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment, startDate?.toISOString(), endDate?.toISOString(), page, filterKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { detail, loading, error, refresh };
};

/**
 * useEngagementConversions — server-side conversions list for the Conversions view.
 *
 * Returns the converted (quote-submitting) sessions in range, each enriched with
 * its HubSpot deal (property name, postcode, stage, value). The cookie-free plane.
 *
 * @param {Object} options { environment, startDate, endDate, enabled }
 * @returns {{ conversions, scanned, capped, lettersFailed, loading, error, refresh }}
 */
export const useEngagementConversions = (options = {}) => {
  const { environment = 'production', startDate, endDate, enabled = true, hideInternal = true } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  // Requests are stamped so a slow earlier fetch (e.g. a 90-day scan) can't land
  // after a faster later one (Today) and overwrite it with stale numbers — the
  // range switch would otherwise silently show the previous range's figures.
  const reqRef = useRef(0);

  const repo = useMemo(() => new EngagementRepository(db, functions), []);
  // Which question the rows answer (the Quote panel's "After a mailshot" view shows them only when it is the
  // question asked now: never the last range's figures under the new range's dates).
  const askKey = `${environment}|${startDate ? startDate.toISOString() : ''}|${endDate ? endDate.toISOString() : ''}|${hideInternal}`;

  const refresh = useCallback(async () => {
    const myReq = reqRef.current + 1;
    reqRef.current = myReq;
    const key = askKey;
    setLoading(true);
    setError(null);
    const run = () =>
      repo.getConversions({ environment, startDate: startDate || null, endDate: endDate || null, hideInternal });
    try {
      let result;
      try {
        result = await run();
      } catch (firstErr) {
        // One silent retry for an idle/stale Safari connection.
        console.warn('[useEngagementConversions] first attempt failed, retrying once:', firstErr?.message);
        result = await run();
      }
      if (reqRef.current !== myReq) return; // superseded by a newer request
      setData(result ? { ...result, askedFor: key } : result);
    } catch (err) {
      console.error('[useEngagementConversions] Load error:', err);
      if (reqRef.current !== myReq) return;
      setError(err);
    } finally {
      if (reqRef.current === myReq) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment, startDate?.toISOString(), endDate?.toISOString(), hideInternal]);

  useEffect(() => {
    if (enabled) refresh();
  }, [refresh, enabled]);

  return {
    conversions: data?.conversions || [],
    // source label → { source, detail?, via? }, for the two-line bar labels
    sourceParts: data?.sourceParts || {},
    scanned: data?.scanned || 0,
    capped: !!data?.capped,
    lettersFailed: !!data?.lettersFailed,
    // true only when the rows held are the answer to the question asked NOW (this range and these toggles)
    fresh: Boolean(data) && data.askedFor === askKey,
    loading,
    error,
    refresh,
  };
};

/**
 * useEngagementVisitors — server-side visitor list for the Visitors (formerly
 * Sessions) view. One row per visitor, grouping sessions that share a
 * hubspotutk or quoteId — see getEngagementVisitors.js for the aggregation.
 *
 * @param {Object} options { environment, startDate, endDate, enabled }
 * @returns {{ visitors, scanned, capped, loading, error, refresh }}
 */
export const useEngagementVisitors = (options = {}) => {
  const { environment = 'production', startDate, endDate, enabled = true, hideInternal = true } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  // Requests are stamped so a slow earlier fetch (e.g. a 90-day scan) can't land
  // after a faster later one (Today) and overwrite it with stale numbers — the
  // range switch would otherwise silently show the previous range's figures.
  const reqRef = useRef(0);

  const repo = useMemo(() => new EngagementRepository(db, functions), []);

  const refresh = useCallback(async () => {
    const myReq = reqRef.current + 1;
    reqRef.current = myReq;
    setLoading(true);
    setError(null);
    const run = () =>
      repo.getVisitors({ environment, startDate: startDate || null, endDate: endDate || null, hideInternal });
    try {
      let result;
      try {
        result = await run();
      } catch (firstErr) {
        // One silent retry for an idle/stale Safari connection.
        console.warn('[useEngagementVisitors] first attempt failed, retrying once:', firstErr?.message);
        result = await run();
      }
      if (reqRef.current !== myReq) return; // superseded by a newer request
      setData(result);
    } catch (err) {
      console.error('[useEngagementVisitors] Load error:', err);
      if (reqRef.current !== myReq) return;
      setError(err);
    } finally {
      if (reqRef.current === myReq) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment, startDate?.toISOString(), endDate?.toISOString(), hideInternal]);

  useEffect(() => {
    if (enabled) refresh();
  }, [refresh, enabled]);

  return {
    visitors: data?.visitors || [],
    // source label → { source, detail?, via? }, for the two-line source cell
    sourceParts: data?.sourceParts || {},
    scanned: data?.scanned || 0,
    capped: !!data?.capped,
    loading,
    error,
    refresh,
  };
};

/**
 * useLinkedSessions — all sessions belonging to the same visitor as `session`:
 * those sharing its `hubspotutk` (consented cross-session link) or its `quoteId`
 * (quote-journey link). Returned oldest-first, with the primary session included.
 * Falls back to just the primary when there's nothing to link on.
 */
export const useLinkedSessions = (session) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  const repo = useMemo(() => new EngagementRepository(db, functions), []);
  const hubspotutk = session?.hubspotutk || null;
  const quoteId = session?.dealId || null; // dealId holds the quote UUID

  const load = useCallback(async () => {
    if (!session) { setSessions([]); return; }
    if (!hubspotutk && !quoteId) { setSessions([session]); return; }

    setLoading(true);
    try {
      const res = await repo.findLinkedSessions({ hubspotutk, quoteId });
      const map = new Map(res.map((s) => [s.id, s]));
      if (!map.has(session.id)) map.set(session.id, session);
      const arr = [...map.values()].sort(
        (a, b) => (a.firstSeen?.getTime() || 0) - (b.firstSeen?.getTime() || 0),
      );
      setSessions(arr);
    } catch (err) {
      console.warn('[useLinkedSessions] load failed:', err?.message);
      setSessions([session]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, hubspotutk, quoteId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [load]);

  return { sessions, loading, refresh: load };
};

/**
 * useSetSessionGroupInternal — manually mark/unmark a visitor's engagement
 * session(s) as internal traffic (setSessionGroupInternalCallable). Resolves
 * the visitor's full linked group server-side, so marking one session marks
 * every linked session consistently. Shared by the Visitors/Conversions lists
 * and the session detail page.
 *
 * @returns {{ setInternal: (opts) => Promise<Object>, marking: boolean }}
 */
export const useSetSessionGroupInternal = () => {
  const repo = useMemo(() => new EngagementRepository(db, functions), []);
  const [marking, setMarking] = useState(false);

  const setInternal = useCallback(async ({ sessionId, hubspotutk = null, quoteId = null, isInternal = true }) => {
    setMarking(true);
    try {
      return await repo.setSessionGroupInternal({ sessionId, hubspotutk, quoteId, isInternal });
    } finally {
      setMarking(false);
    }
  }, [repo]);

  return { setInternal, marking };
};
